import type { LatestPort } from "../devices/types";
import type { CalcOp, SignalType } from "./types";

export function toNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

/**
 * Le o sinal pedido (`v` / `acc` / `s`) de um snapshot de porta e converte
 * para `number | null`.
 */
export function resolveSignalValue(
  port: LatestPort | undefined,
  signal: SignalType,
): number | null {
  if (!port) return null;
  const raw = signal === "s" ? port.s : signal === "v" ? port.v : port.acc;
  if (typeof raw === "boolean") return raw ? 1 : 0;
  return toNumber(raw);
}

/**
 * Avalia uma operacao sobre valores numericos ja resolvidos. Mantem-se uma
 * funcao pura (sem conhecimento de `LatestPort`) para que o motor de
 * extrapolacao do front possa alimentar valores derivados de telemetria sem
 * mudar a API.
 */
export function evaluateCalc(op: CalcOp, values: Array<number | null>): number | null {
  if (values.some((value) => value === null || !Number.isFinite(value))) {
    return null;
  }

  const numeric = values.filter(
    (value): value is number => value !== null && Number.isFinite(value),
  );

  if (numeric.length === 0) return null;

  switch (op) {
    case "avg":
      return numeric.reduce((acc, value) => acc + value, 0) / numeric.length;
    case "sum":
      return numeric.reduce((acc, value) => acc + value, 0);
    case "min":
      return Math.min(...numeric);
    case "max":
      return Math.max(...numeric);
    case "add":
      return numeric.length === 2 ? numeric[0] + numeric[1] : null;
    case "sub":
      return numeric.length === 2 ? numeric[0] - numeric[1] : null;
    case "mul":
      return numeric.length === 2 ? numeric[0] * numeric[1] : null;
    case "div":
      return numeric.length === 2 && numeric[1] !== 0 ? numeric[0] / numeric[1] : null;
  }
}
